import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import muziek from '../assets/wrapfeest/mexican-hat-dance.mp3';
import vlag from '../assets/wrapfeest/mexicaanse-vlag.png';
import danser1 from '../assets/wrapfeest/sombrero-danser-1.png';
import danser2 from '../assets/wrapfeest/sombrero-danser-2.png';
import danser3 from '../assets/wrapfeest/sombrero-danser-3.png';

const KLEUREN = ['#006847', '#ffffff', '#ce1126', '#e0a943'];
const DANSERS = [danser1, danser2, danser3];
const AANTAL_CONFETTI = 220;
const AANTAL_DANSERS = 5;
const DUUR_MS = 5000;
const VOLUME = 0.55;
const UITFADEN_MS = 800;

interface Props {
  /** Verhoog om een nieuw feestje te starten; 0 = nooit getriggerd. */
  trigger: number;
}

/** Viert het kiezen van een wrap: heel veel confetti, dansende mensen met
 *  sombrero's, El Jarabe Tapatío eronder, en het scherm kleurt 5 seconden mee
 *  met de Mexicaanse vlag. Puur decoratief — geen pointer-events, dus de app
 *  blijft gewoon bruikbaar terwijl het feestje loopt. */
export function WrapFeest({ trigger }: Props) {
  const [actief, setActief] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (trigger === 0) return;
    setActief(true);

    // El Jarabe Tapatío zolang het feestje duurt. Het klikken op de suggestie
    // is het gebruikersgebaar dat de browser nodig heeft om te mogen spelen;
    // lukt het toch niet, dan gaat het feestje gewoon zonder geluid door.
    const audio = (audioRef.current ??= new Audio(muziek));
    audio.currentTime = 0;
    audio.volume = VOLUME;
    void audio.play().catch(() => {});

    const stappen = 16;
    let fade: ReturnType<typeof setInterval>;
    const uitfaden = setTimeout(() => {
      let stap = 0;
      fade = setInterval(() => {
        stap += 1;
        audio.volume = Math.max(0, VOLUME * (1 - stap / stappen));
        if (stap >= stappen) clearInterval(fade);
      }, UITFADEN_MS / stappen);
    }, DUUR_MS - UITFADEN_MS);

    // Ook pauzeren, niet alleen verbergen: loopt het feestje gewoon af, dan
    // draait de cleanup hieronder niet (de deps zijn niet veranderd) en speelt
    // het nummer stilletjes — het volume staat op 0 — door tot het einde.
    const timeout = setTimeout(() => {
      setActief(false);
      audio.pause();
    }, DUUR_MS);

    return () => {
      clearTimeout(timeout);
      clearTimeout(uitfaden);
      clearInterval(fade);
      audio.pause();
    };
  }, [trigger]);

  const confetti = useMemo(
    () =>
      Array.from({ length: AANTAL_CONFETTI }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 1.2,
        duration: 2.6 + Math.random() * 2.2,
        kleur: KLEUREN[i % KLEUREN.length],
        breedte: 6 + Math.random() * 8,
        spin: 180 + Math.random() * 720,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trigger],
  );

  const dansers = useMemo(
    () =>
      Array.from({ length: AANTAL_DANSERS }, (_, i) => ({
        id: i,
        bron: DANSERS[i % DANSERS.length],
        top: 18 + Math.random() * 62,
        hoogte: 120 + Math.random() * 70,
        delay: Math.random() * 1.2,
        duration: 3.2 + Math.random() * 1.8,
        danstempo: 0.36 + Math.random() * 0.2,
        omgekeerd: i % 2 === 1,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trigger],
  );

  if (!actief) return null;

  return (
    // De key zorgt dat React het hele blok opnieuw mount bij een volgende
    // wrap; anders lopen de CSS-animaties niet opnieuw en zie je niets.
    <div key={trigger} className="vs-wrapfeest" aria-hidden="true">
      <div className="vs-wrapfeest__vlag" style={{ backgroundImage: `url(${vlag})` }} />

      {dansers.map((d) => (
        <div
          key={`danser-${d.id}`}
          className={
            d.omgekeerd ? 'vs-wrapfeest__reis vs-wrapfeest__reis--terug' : 'vs-wrapfeest__reis'
          }
          style={{
            top: `${d.top}vh`,
            animationDelay: `${d.delay}s`,
            animationDuration: `${d.duration}s`,
          }}
        >
          <img
            className="vs-wrapfeest__danser"
            src={d.bron}
            alt=""
            style={{ height: `${d.hoogte}px`, animationDuration: `${d.danstempo}s` }}
          />
        </div>
      ))}

      {confetti.map((c) => (
        <span
          key={c.id}
          className="vs-wrapfeest__confetti"
          style={
            {
              left: `${c.left}vw`,
              backgroundColor: c.kleur,
              width: `${c.breedte}px`,
              height: `${c.breedte * 0.4}px`,
              animationDelay: `${c.delay}s`,
              animationDuration: `${c.duration}s`,
              '--vs-confetti-spin': `${c.spin}deg`,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}
